import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";

import { db } from "../../firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";

import { useUser } from '../../store/UserProvider';
import { saveUserLog } from './LogandUtils'; // 경로에 맞게 수정

import SEO from '../SEO';
import Popup from "../Popup";
import Loading from "../Loading";
import MyPageMenu from "./MyPageMenu";
import CheckBoxSelector from "./CheckBoxSelector";
import MusicardSourcePopup from "./MusicardSourcePopup";
import MusicardSelectorPopup from "./MusicardSelectorPopup";

import styleClasses from "./css/MyPage.module.scss";

function MembersDetail() {
  const { memberID }  = useParams();
  
  const { user, userLoading } = useUser();

  const navigate      = useNavigate();
  const location      = useLocation();

  const isSubmitting  = useRef(false);
  
  const [userID, setUserID]                     = useState(null);
  const [buttonLoading, setButtonLoading]       = useState(false);
  const [selectedSource, setSelectedSource]     = useState({});

  const [openPopup, setOpenPopup]               = useState(false);
  const [popupTitle, setPopupTitle]             = useState('');
  const [popupButtonText, setPopupButtonText]   = useState('');
  const [customFunction, setCustomFunction]     = useState('');

  const [openSelectorPopup, setOpenSelectorPopup] = useState(false);

  const [openSourcePopup, setOpenSourcePopup]   = useState(false);

  // 초기 멤버 데이터: description, image, link, name, position, sns
  const [memberData, setMemberData] = useState({
    description: "",
    image: "",
    link: "",
    name: "",
    position: "",
    sns: {}
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [positionOptions, setPositionOptions] = useState([]);
  const [hasUpdatedSource, setHasUpdatedSource] = useState(false);

  // SNS 추가를 위한 state
  const [newSnsPlatform, setNewSnsPlatform] = useState("");

  // 사용 가능한 SNS 목록 (예시)
  const availableSNSPlatforms = ["Instagram", "YouTube", "X", "TikTok", "Facebook", "Thread", "LinkedIn", "Naver Blog", "Kakao Opentalk"];
  // 현재 SNS 플랫폼들 (소문자로 저장됨)
  const currentSnsPlatforms = Object.keys(memberData.sns);
  const remainingSnsPlatforms = availableSNSPlatforms.filter(
    (sns) => !currentSnsPlatforms.includes(sns)
  );

  // Firestore에서 멤버 데이터 및 메타데이터(포지션 옵션) 불러오기 (수정 모드)
  const fetchMetadata = useCallback(async () => {
    try {
      const positionsRef = doc(db, "metadata", "positions");
      const positionsSnap = await getDoc(positionsRef);
      if (positionsSnap.exists()) setPositionOptions(positionsSnap.data().list);
    } catch (error) {
      console.error("메타데이터 불러오기 실패:", error);
    }
  }, []);
  const fetchMember = useCallback(async () => {
    try {
      if (memberID !== "new") {
        console.log(userID);
        const memberRef = doc(db, "users_data", userID, "members", memberID);
        const memberSnap = await getDoc(memberRef);
        if (memberSnap.exists()) {
          setError('');
          setMemberData(memberSnap.data());
        } else {
          setError("멤버 데이터를 찾을 수 없습니다.");
        }
      }
    } catch (err) {
      console.error("멤버 데이터 불러오기 오류:", err);
      setError("멤버 데이터를 불러오는 중 오류가 발생했습니다.");
    }
    setLoading(false);
  }, [memberID, userID]);

  // 🔹 로그인 확인 후 멤버 데이터 조회
  useEffect(() => {
    if (userLoading) return;

    if (user?.userID) {
      setUserID(user.userID);
      setLoading(true);
      fetchMetadata();
      fetchMember();
    } else {
      navigate('/login');
    }
  }, [memberID, navigate, userLoading, user?.userID, fetchMember, fetchMetadata]);

  // 입력값 변경 핸들러
  const handleChange = (e) => {
    const { name, value } = e.target;
    setMemberData((prev) => ({ ...prev, [name]: value }));
  };

  // SNS 입력 변경 핸들러
  const handleSnsChange = (platform, value) => {
    setMemberData((prev) => ({
      ...prev,
      sns: {
        ...prev.sns,
        [platform]: value,
      },
    }));
  };

  // SNS 추가 핸들러
  const handleAddSns = () => {
    if (newSnsPlatform.trim() === "" || memberData.sns[newSnsPlatform]) return;
    setMemberData((prev) => ({
      ...prev,
      sns: { ...prev.sns, [newSnsPlatform]: "" },
    }));
    setNewSnsPlatform("");
  };

  // SNS 삭제 핸들러
  const handleRemoveSns = (platform) => {
    setMemberData((prev) => {
      const updated = { ...prev.sns };
      delete updated[platform];
      return { ...prev, sns: updated };
    });
  };

  // 🔹 소스 팝업 - 앨범 선택 (트랙)
  const handleTrackSelect = (newID, newThumbnail, newTitle) => {
    setMemberData((prev) => ({
      ...prev,
      link: newID || '',          // 🔹 배경 음원 YouTube ID
      image: newThumbnail || '',  // 🔹 CD 썸네일 URL
    }));
    setOpenSourcePopup(false);
  };

  // 🔹 소스 팝업 - 액티비티 선택
  const handleActivitySelect = (activity) => {
    setMemberData((prev) => ({
      ...prev,
      link: activity.videoId || "", // 🔹 배경 음원 YouTube ID
      image: activity.photo || '',  // 🔹 CD 썸네일 URL
    }));
    setOpenSourcePopup(false);
  }

  // useEffect: SourceSelector에서 선택한 데이터 (링크 및 이미지) 처리
  useEffect(() => {
    if (
      !loading &&
      !hasUpdatedSource &&
      location.state &&
      location.state.selectedSource
    ) {
      const { selectedSource } = location.state;
      let newImage = "";
      let newLink = "";
      
      // videoId가 있는 경우 (SourceSelector의 소스 반환일 경우)
      if (selectedSource.videoId) {
        const videoId = selectedSource.videoId;
        newLink = `https://www.youtube.com/watch?v=${videoId}`;
        newImage = `https://img.youtube.com/vi/${videoId}/0.jpg`;
      } 
      // MusicardSelector에서 반환한 경우: profileImage와 link 사용
      else if (selectedSource.profileImage) {
        newImage = selectedSource.profileImage;
        newLink = selectedSource.link;
      } 
      // 그 외 기존 로직
      else {
        newImage = selectedSource.cover || selectedSource.photo || selectedSource.icon || "";
        newLink = selectedSource.url || selectedSource.link || "";
      }
      
      setMemberData((prev) => ({
        ...prev,
        // 멤버 이름 업데이트: selectedSource.name이 있으면 반영
        name: selectedSource.name || prev.name,
        // 포지션 업데이트: selectedSource.positions가 존재하면 object면 값들을 '·'로 연결, 아니면 그대로 사용
        position: selectedSource.positions
          ? (typeof selectedSource.positions === "object"
              ? Object.values(selectedSource.positions).join("·")
              : selectedSource.positions)
          : prev.position,
        image: newImage,
        link: newLink,
        sns: { ...prev.sns, ...selectedSource.sns }
      }));
      
      setHasUpdatedSource(true);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [loading, location, hasUpdatedSource]);
  
  // 🔹 팝업에서 가져온 뮤지션 데이터
  useEffect(() => {
    let newImage = "";
    let newLink = "";
    // videoId가 있는 경우 (SourceSelector의 소스 반환일 경우)
    if (selectedSource.videoId) {
      const videoId = selectedSource.videoId;
      newLink = `https://www.youtube.com/watch?v=${videoId}`;
      newImage = `https://img.youtube.com/vi/${videoId}/0.jpg`;
    } 
    // MusicardSelector에서 반환한 경우: profileImage와 link 사용
    else if (selectedSource.profileImage) {
      newImage = selectedSource.profileImage;
      newLink = selectedSource.link;
    } 
    // 그 외 기존 로직
    else {
      newImage = selectedSource.cover || selectedSource.photo || selectedSource.icon || "";
      newLink = selectedSource.url || selectedSource.link || "";
    }

    setMemberData((prev) => ({
      ...prev,
      // 멤버 이름 업데이트: selectedSource.name이 있으면 반영
      name: selectedSource.name || prev.name,
      // 포지션 업데이트: selectedSource.positions가 존재하면 object면 값들을 '·'로 연결, 아니면 그대로 사용
      position: selectedSource.positions
        ? (typeof selectedSource.positions === "object"
            ? Object.values(selectedSource.positions).join("·")
            : selectedSource.positions)
        : prev.position,
      image: newImage,
      link: newLink,
      sns: { ...prev.sns, ...selectedSource.sns }
    }));

    setHasUpdatedSource(true);
  }, [selectedSource])

  // 저장 핸들러: 새 멤버 추가 또는 수정
  const handleSave = async (e) => {
    e.preventDefault();
    if (!memberData.name.trim()) {
      setOpenPopup(true);
      setPopupTitle('멤버 이름을 입력하세요.');
      setPopupButtonText('');
      setCustomFunction('');
      return;
    } else if (memberData.position === '') {
      setOpenPopup(true);
      setPopupTitle('포지션을 선택하세요.');
      setPopupButtonText('');
      setCustomFunction('');
      return;
    }
    
    try {
      if (isSubmitting.current) return; // 실행 중일 경우 중복 클릭 방지
      isSubmitting.current = true;      // 버튼 비활성화
      setButtonLoading(true);           // 버튼 로딩 표시

      // 새 멤버 추가 시 문서 ID로 memberData.name 사용, 수정 시 memberID 사용
      const id = memberID === "new" ? memberData.name : memberID;
      const memberRef = doc(db, "users_data", userID, "members", id);
      // 사용자 멤버 문서를 저장 (act 필드는 그대로 유지)
      await setDoc(memberRef, memberData);
      
      // 이미 뮤지카드에 내보낸 경우 act 필드에 저장된 뮤지카드 ID를 확인하고,
      // 해당 뮤지카드의 멤버 문서도 업데이트합니다.
      const memberSnap = await getDoc(memberRef);
      if (memberSnap.exists()) {
        const mData = memberSnap.data();
        if (mData.act) {
          // act 객체의 각 키(뮤지카드 ID)에 대해 값이 현재 멤버 id와 일치하면 업데이트
          for (const musicianCardId in mData.act) {
            if (mData.act[musicianCardId] === id) {
              // 뮤지카드의 멤버 문서 경로: musicians/[뮤지카드ID]/members/[id]
              const musicianMemberRef = doc(db, "musicians", musicianCardId, "members", id);
              // 업데이트: 최근 수정 시간을 갱신하고, 전체 memberData를 병합 업데이트
              await setDoc(musicianMemberRef, { updatedAt: new Date() });
              await setDoc(musicianMemberRef, memberData, { merge: true });
              await saveUserLog('musicard_logs', musicianCardId, 'update', musicianMemberRef.path, 'MembersDetail');
            }
          }
        }
      }

      isSubmitting.current = false;   // 버튼 활성화
      setButtonLoading(false);        // 버튼 로딩 숨김

      setOpenPopup(true);
      setPopupTitle('멤버 정보가 저장되었습니다.');
      setPopupButtonText('목록으로 이동');
      setCustomFunction(() => () => navigate("/my-page/members/"));

      // 추가일 경우 필드 초기화
      if (memberID === 'new') {
        setMemberData({
          description: "",
          image: "",
          link: "",
          name: "",
          position: "",
          sns: {}
        });
      }
    } catch (err) {
      console.error("멤버 저장 오류:", err);

      isSubmitting.current = false;   // 버튼 활성화
      setButtonLoading(false);
      
      setOpenPopup(true);
      setPopupTitle("멤버 저장 중 오류가 발생했습니다.");
      setPopupButtonText('');
      setCustomFunction('');
    }
  };  

  return (
    <>
      <SEO title={`멤버 ${ memberID === 'new' ? '추가' : '수정' }`} />
      <div className={styleClasses.myPage}>
        <div className={styleClasses.inner}>
          <div className={styleClasses.title}>
            <h1>Members {memberID === "new" ? "(새 멤버 추가)" : ""}</h1>
          </div>
          <div className={styleClasses.editContent}>
            <div className={styleClasses.leftContent}>
              <MyPageMenu currentPage='members' />
            </div>
            <div className={styleClasses.rightContent}>
              {
                loading
                ?
                (
                  <Loading />
                )
                :
                error
                ?
                (
                  <p className={ styleClasses.error }>{ error }</p>
                )
                :
                (
                  <form className={styleClasses.memberDetail} onSubmit={handleSave}>
                    <div className={ styleClasses.detailBox }>
                      <div className="commonForm">
                        <div className="formRow fullWidth">
                          <div className="labelBox">
                            <label>멤버 이름</label>
                          </div>
                          <div className="fieldBox">
                            <input
                              type="text"
                              name="name"
                              value={memberData.name}
                              onChange={handleChange}
                              placeholder="멤버 이름"
                            />
                          </div>
                          <div className={`commonButton ${ styleClasses.rightButtons }`}>
                            <div className={`commonIcon toolTip ${ styleClasses.topButtonToolTip }`}>
                              <i className="questionBlack w20"></i>
                              <div className="toolTipText left">
                                <p>이미 솔로 뮤지카드를 가진 팀 멤버라면, <br />팀 뮤지카드와 연결해보세요!</p>
                              </div>
                            </div>
                            {/* <button type="button" onClick={() => navigate(`/my-page/members/${memberID}/musicard-selector`, { state: {} }) }> */}
                            <button type="button" onClick={() => setOpenSelectorPopup(true) }>
                              뮤지카드 연결하기
                            </button>
                          </div>
                        </div>
                        <CheckBoxSelector
                          label="포지션 선택"
                          options={positionOptions}
                          selectedItems={memberData.position ? memberData.position.split("·") : []}
                          setSelectedItems={(newItems) =>
                            setMemberData((prev) => ({ ...prev, position: newItems.join("·") }))
                          }
                          styleClasses={styleClasses}
                        />
                        <div className="formRow fullWidth">
                          <div className="labelBox">
                            <label>SNS 정보</label>
                          </div>
                          <div className="fieldBox">
                            <div className={styleClasses.snsGroup}>
                              {
                                currentSnsPlatforms.map((snsPlatform) => (
                                  <div key={snsPlatform} className={styleClasses.snsInputRow}>
                                    <div className={`labelBox ${ styleClasses.customLabelBox }`}>
                                      <label>
                                        {snsPlatform.charAt(0).toUpperCase() + snsPlatform.slice(1)}
                                      </label>
                                    </div>
                                    <div className={`fieldBox ${ styleClasses.customFieldBox }`}>
                                      <input
                                        type="text"
                                        name={snsPlatform}
                                        value={memberData.sns[snsPlatform] || ""}
                                        placeholder={`Enter ${snsPlatform} URL`}
                                        onChange={(e) => handleSnsChange(snsPlatform, e.target.value)}
                                      />
                                    </div>
                                    <div className={ styleClasses.customButtonBox }>
                                      <div className="commonButton fullWidth">
                                        <button type="button" className="secondary" onClick={() => handleRemoveSns(snsPlatform)}>삭제</button>
                                      </div>
                                    </div>
                                  </div>
                                ))
                              }
                              <div className={styleClasses.addSnsRow}>
                                <div className={`labelBox ${ styleClasses.customLabelBox }`}>
                                  <label>SNS 선택</label>
                                </div>
                                <div className={`fieldBox ${ styleClasses.customFieldBox }`}>
                                  <select value={newSnsPlatform} onChange={(e) => setNewSnsPlatform(e.target.value)}>
                                  <option value="">선택</option>
                                  {remainingSnsPlatforms.map((sns) => (
                                      <option key={sns} value={sns}>{sns}</option>
                                  ))}
                                  </select>
                                </div>
                                <div className={ styleClasses.customButtonBox }>
                                  <div className="commonButton fullWidth">
                                    <button type="button" onClick={handleAddSns}>추가</button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className={ styleClasses.groupBox }>
                          <div className="formRow fullWidth">
                              <div className="labelBox">
                                  <label>이미지 URL</label>
                              </div>
                              <div className="fieldBox">
                                  <input
                                  type="text"
                                  name="image"
                                  value={memberData.image}
                                  onChange={handleChange}
                                  placeholder="이미지 URL"
                                  />
                              </div>
                          </div>
                          <div className="formRow fullWidth">
                            <div className="labelBox">
                                <label>링크 URL</label>
                            </div>
                            <div className="fieldBox">
                                <input
                                type="text"
                                name="link"
                                value={memberData.link}
                                onChange={handleChange}
                                placeholder="링크 URL"
                                />
                            </div>
                            {/* 링크 가져오기 버튼 */}
                            <div className={`commonButton ${ styleClasses.rightButtons }`}>
                              {/* <button type="button" onClick={ () => navigate(`/my-page/members/${memberID}/source-selector`, { state: {} }) }> */}
                              <div className={`commonIcon toolTip ${ styleClasses.topButtonToolTip }`}>
                                <i className="questionBlack w20"></i>
                                <div className="toolTipText bottom">
                                  <p>이미 저장한 음원(Archive) 및 활동(Activity) 데이터에서 <br />이미지 및 링크 정보를 가져올 수 있습니다.</p>
                                </div>
                              </div>
                              <button type="button" onClick={ () => setOpenSourcePopup(true) }>
                                Archive·Activity에서 가져오기
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="formRow fullWidth">
                          <div className="labelBox">
                            <label>설명</label>
                          </div>
                          <div className="fieldBox">
                            <textarea
                              name="description"
                              value={memberData.description}
                              onChange={handleChange}
                              placeholder="멤버 설명"
                            />
                          </div>
                        </div>
                        <div className={ styleClasses.buttonWrap }>
                          <div className={ styleClasses.topButtons }>
                            <div className="commonButton fullwidth">
                                <Link to={`/my-page/members/`} className='secondary'>목록</Link>
                            </div>
                            <div className="commonButton fullWidth">
                              <button type="submit" className="black">
                                저장하기
                                <div className={`buttonLoading ${ buttonLoading ? 'active' : undefined }`}>
                                  <div></div>
                                  <div></div>
                                  <div></div>
                                </div>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </form>
                )
              }
            </div>
          </div>
        </div>
      </div>
      <MusicardSelectorPopup
        onSelect=''
        open={ openSelectorPopup }
        setOpenPopup={ setOpenSelectorPopup }
        setSelectedSource={ setSelectedSource }
      />
      <MusicardSourcePopup
        open={ openSourcePopup }
        setOpenPopup={ setOpenSourcePopup }
        handleTrackSelect={ handleTrackSelect }
        handleActivitySelect={ handleActivitySelect }
      />
      <Popup
        open={ openPopup }
        setOpenPopup={ setOpenPopup }
        title={ popupTitle }
        buttonText={ popupButtonText }
        buttonFunction={ customFunction }
      />
    </>
  );
}

export default MembersDetail;